import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth, api } from '../context/AuthContext';

const STORAGE_KEY = 'seen_booking_request_ids';
const POLL_MS = 30000;

const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); }
  catch { return new Set(); }
};

const saveSeen = (set) => {
  try {
    const arr = Array.from(set).slice(-200); // cap
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch { /* ignore */ }
};

// Hook: while user is logged in, poll for new pending booking requests
// (as a creator) and fire an in-app toast + native browser Notification when
// a request that hasn't been seen before appears.
export const useBookingPush = () => {
  const { user } = useAuth();
  const timerRef = useRef(null);
  const bootRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const seen = loadSeen();

    // Request permission once on first mount (silent if already answered)
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }

    const tick = async () => {
      try {
        const data = await api(`/api/bookings/requests/${user.id}`);
        const reqs = data.requests || [];
        const fresh = reqs.filter(r => !seen.has(r.id));
        // On first tick after login, mark all existing as seen without
        // firing notifications (so we don't spam on refresh).
        if (!bootRef.current) {
          reqs.forEach(r => seen.add(r.id));
          saveSeen(seen);
          bootRef.current = true;
        } else if (fresh.length > 0) {
          fresh.forEach(r => {
            seen.add(r.id);
            const who = r.requester_name || r.requester_username || 'Someone';
            const msg = `${who} requested a ${r.minutes}-min session ($${Number(r.price || 0).toFixed(0)}).`;
            // in-app toast
            toast.success('New booking request', {
              description: msg,
              action: {
                label: 'View',
                onClick: () => { window.location.href = '/notifications'; },
              },
            });
            // native browser notification
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                const n = new Notification('New booking request · 6th Mate', {
                  body: msg,
                  icon: '/favicon.ico',
                  tag: `booking-${r.id}`,
                });
                n.onclick = () => { window.focus(); window.location.href = '/notifications'; };
              } catch { /* ignore */ }
            }
          });
          saveSeen(seen);
        }
      } catch { /* silently ignore polling errors */ }
      if (!cancelled) timerRef.current = setTimeout(tick, POLL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [user?.id]);
};

export default useBookingPush;
